import { optimizelyClient } from '@frontend/contexts/optimizelyClient';
import { useOpalUser, useOperations } from '@frontend/hooks';
import { InstanceOperationsName } from '@frontend/types';

/**
 * Guard that grants access when the instance has one of the given capabilities
 * OR one of the given feature flags is enabled.
 *
 * Prefer `CanInstance` for capability-only gating. Use this component only when a
 * genuine feature-flag gate is still required alongside capabilities — e.g. an
 * unreleased flag such as `vau` that is not yet provisioned as an instance
 * capability.
 *
 * @param capabilities - instance capabilities to check (OR logic)
 * @param featureFlags - feature flag names to check (OR logic)
 * @param children - children components to render if access granted
 * @param fallback - element to render when access is denied (defaults to null)
 * @returns JSX.Element - children if access granted, otherwise fallback element
 */
function CapabilityOrFlagGuard({
  capabilities,
  featureFlags = [],
  children,
  fallback = null,
}: {
  capabilities: InstanceOperationsName[];
  featureFlags?: string[];
  children: JSX.Element;
  fallback?: JSX.Element | null;
}) {
  const { canInstance } = useOperations();
  const { opalUser } = useOpalUser();

  // Check new capability
  const hasCapability = canInstance(capabilities);

  // Check each old feature flag (if any) using optimizelyClient imperatively
  const hasOldFlag = featureFlags.some(flagName => {
    return optimizelyClient.isFeatureEnabled(flagName, opalUser?.id || '', {
      email: opalUser?.email,
      instanceId: opalUser?.instance?.instance_id,
      customerId: opalUser?.instance?.customer_id,
      productSku: opalUser?.instance?.product_sku,
      productId: opalUser?.instance?.product_id,
    });
  });

  // Grant access if either capability OR old flag is enabled
  if (!hasCapability && !hasOldFlag) {
    return fallback;
  }

  return children;
}

export default CapabilityOrFlagGuard;
